// Market Watch Data Analysis Functions

import {
  NumericColumnData,
  CategoricalColumnData,
  VarianceData,
  ColumnStats,
} from "./types";
import { DATA_DETECTION } from "../../../shared/constants";

// Analyze AI report to find emphasized metrics
export const analyzeAIEmphasis = (htmlReport: string, summary: string): Record<string, number> => {
  const text = (htmlReport + " " + summary).toLowerCase();
  const emphasis: Record<string, number> = {};

  // Count mentions and context importance
  const patterns: Record<string, RegExp[]> = {
    demand: [/demand/g, /volume/g, /search.*volume/g, /forecast/g],
    price: [/price/g, /pricing/g, /\$\d+/g, /cost/g, /avg.*price/g],
    rating: [/rating/g, /star/g, /review.*score/g],
    reviews: [/review/g, /feedback/g, /customer.*review/g],
    keywords: [/keyword/g, /search.*term/g, /query/g, /top.*keyword/g],
    asins: [/asin/g, /product/g, /competitor/g, /market.*share/g],
    market_share: [/market.*share/g, /share.*market/g, /dominat/g, /leader/g],
    trend: [/trend/g, /growth/g, /increase/g, /decrease/g, /change/g],
    competition: [/compet/g, /rival/g, /versus/g, /vs\./g],
    opportunity: [/opportun/g, /potential/g, /gap/g, /untapped/g],
  };

  for (const [key, regexes] of Object.entries(patterns)) {
    let count = 0;
    for (const regex of regexes) {
      const matches = text.match(regex);
      count += matches ? matches.length : 0;
    }
    emphasis[key] = count;
  }

  return emphasis;
};

// Analyze data variance to find interesting columns
export const analyzeDataVariance = (headers: string[], rows: any[][]): Map<number, VarianceData> => {
  const variance = new Map<number, VarianceData>();

  headers.forEach((_, idx) => {
    const values = rows.map(r => r[idx]);
    const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v) && isFinite(v));

    if (numericValues.length > rows.length * DATA_DETECTION.MIN_NUMERIC_RATIO) {
      const mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
      const squaredDiffs = numericValues.map(v => Math.pow(v - mean, 2));
      const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / numericValues.length;
      const stdDev = Math.sqrt(avgSquaredDiff);
      const coefficient = mean !== 0 ? (stdDev / Math.abs(mean)) : 0;
      const range = Math.max(...numericValues) - Math.min(...numericValues);
      const distinctRatio = new Set(numericValues).size / numericValues.length;

      variance.set(idx, { coefficient, range, distinctRatio });
    } else {
      // Categorical variance
      const distinctValues = new Set(values.filter(v => v != null && v !== ""));
      const distinctRatio = distinctValues.size / values.length;
      variance.set(idx, { coefficient: distinctRatio, range: 0, distinctRatio });
    }
  });

  return variance;
};

// Calculate statistical insights for a numeric column
export const calculateColumnStats = (values: number[]): ColumnStats => {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const median = n % 2 === 0 ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2 : sorted[Math.floor(n / 2)];
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / n);
  const q1 = sorted[Math.floor(n * 0.25)];
  const q3 = sorted[Math.floor(n * 0.75)];

  return { mean, median, stdDev, min: sorted[0], max: sorted[n - 1], q1, q3 };
};

// Detect column semantic type from name
export const detectColumnType = (name: string): string => {
  if (name.includes("price") || name.includes("cost")) return "currency";
  if (name.includes("rating") || name.includes("score")) return "rating";
  if (name.includes("review") || name.includes("count")) return "count";
  if (name.includes("volume") || name.includes("demand") || name.includes("forecast")) return "volume";
  if (name.includes("percent") || name.includes("share") || name.includes("rate")) return "percentage";
  if (name.includes("rank") || name.includes("position")) return "rank";
  return "number";
};

// Detect available numeric columns for potential KPIs
export const detectNumericColumns = (headers: string[], rows: any[][]): Map<number, NumericColumnData> => {
  const numericCols = new Map<number, NumericColumnData>();

  headers.forEach((header, idx) => {
    const values = rows.map(r => parseFloat(r[idx])).filter(v => !isNaN(v) && isFinite(v));
    if (values.length > rows.length * DATA_DETECTION.MIN_NUMERIC_RATIO) { // At least 50% numeric
      const sum = values.reduce((a, b) => a + b, 0);
      numericCols.set(idx, {
        name: header,
        type: detectColumnType(header.toLowerCase()),
        sum,
        avg: sum / values.length,
        min: Math.min(...values),
        max: Math.max(...values),
      });
    }
  });

  return numericCols;
};

// Detect categorical columns for charts
export const detectCategoricalColumns = (headers: string[], rows: any[][]): Map<number, CategoricalColumnData> => {
  const catCols = new Map<number, CategoricalColumnData>();

  headers.forEach((header, idx) => {
    const valueCounts: Record<string, number> = {};
    rows.forEach(r => {
      const val = String(r[idx] || "").trim();
      if (val) valueCounts[val] = (valueCounts[val] || 0) + 1;
    });

    const uniqueCount = Object.keys(valueCounts).length;
    // Good for charts: 2-50 unique values
    if (uniqueCount >= DATA_DETECTION.MIN_CATEGORICAL_VALUES && uniqueCount <= DATA_DETECTION.MAX_CATEGORICAL_VALUES) {
      const topValues = Object.entries(valueCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([value, count]) => ({ value, count }));

      catCols.set(idx, {
        name: header,
        uniqueValues: uniqueCount,
        topValues,
      });
    }
  });

  return catCols;
};

// Helper to calculate meaningful variance in data
export const hasSignificantVariance = (values: number[]): boolean => {
  if (values.length < DATA_DETECTION.MIN_VARIANCE_SAMPLE) return false;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  const cv = mean > 0 ? Math.sqrt(variance) / mean : 0;
  return cv > DATA_DETECTION.VARIANCE_SIGNIFICANCE; // At least 20% coefficient of variation
};
